/**
 * Ant Design theme config. Seed colors that Antd mixes (hover / active /
 * danger) must be real hex — CSS custom properties cannot be parsed, so
 * those derived shades fall back to black.
 */
import { theme as antdTheme, type ThemeConfig } from "antd";

/** Hex equivalents of --color-danger / warning / success in index.css. */
export const ANTD_COLOR_ERROR = "#DC2828";
export const ANTD_COLOR_WARNING = "#F59F0A";
export const ANTD_COLOR_SUCCESS = "#29A36A";

/** Theme config for a given primary hex (tenant brand or live preview). */
export function buildAntdTheme(colorPrimary: string): ThemeConfig {
  return {
    algorithm: antdTheme.defaultAlgorithm,
    token: {
      colorPrimary,
      colorError: ANTD_COLOR_ERROR,
      colorWarning: ANTD_COLOR_WARNING,
      colorSuccess: ANTD_COLOR_SUCCESS,
      colorBgBase: "var(--color-background)",
      colorTextBase: "var(--color-foreground)",
      colorBorder: "var(--color-border)",
      colorBgContainer: "var(--color-card)",
      colorBgElevated: "var(--color-card)",
      colorBgLayout: "var(--color-background)",
      colorTextSecondary: "var(--color-muted-foreground)",
      colorTextDescription: "var(--color-muted-foreground)",
      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 6,
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: 13,
      fontSizeSM: 12,
      fontSizeLG: 14,
      controlHeight: 36,
      controlHeightSM: 28,
    },
    components: {
      Button: {
        fontWeight: 500,
        colorTextLightSolid: "#ffffff",
      },
      Input: {
        paddingBlock: 8,
      },
      Message: {
        contentBg: "var(--color-card)",
        colorText: "var(--color-foreground)",
      },
      Notification: {
        colorBgElevated: "var(--color-card)",
        colorText: "var(--color-foreground)",
        colorTextHeading: "var(--color-foreground)",
      },
      Alert: {
        colorText: "var(--color-foreground)",
        colorTextHeading: "var(--color-foreground)",
        colorErrorBg: "hsl(0 72% 96%)",
        colorErrorBorder: "hsl(0 72% 85%)",
        colorSuccessBg: "hsl(152 60% 95%)",
        colorSuccessBorder: "hsl(152 60% 80%)",
        colorWarningBg: "hsl(38 92% 95%)",
        colorWarningBorder: "hsl(38 92% 80%)",
        colorInfoBg: "hsl(210 60% 95%)",
        colorInfoBorder: "hsl(210 60% 80%)",
      },
      Tag: {
        defaultBg: "var(--color-muted)",
        defaultColor: "var(--color-foreground)",
      },
      DatePicker: {
        colorBgContainer: "var(--color-card)",
        colorBgElevated: "var(--color-card)",
        cellHoverBg: "var(--color-muted)",
        colorPrimary,
        cellActiveWithRangeBg: colorPrimary,
        controlItemBgActive: "var(--color-sidebar-accent)",
      },
      Select: {
        colorBgElevated: "var(--color-card)",
        colorText: "var(--color-foreground)",
        colorTextPlaceholder: "var(--color-muted-foreground)",
        selectorBg: "var(--color-card)",
        optionActiveBg: "var(--color-muted)",
        optionSelectedBg: "var(--color-sidebar-accent)",
        optionSelectedColor: "var(--color-sidebar-accent-foreground)",
        hoverBorderColor: colorPrimary,
        activeBorderColor: colorPrimary,
      },
      Table: {
        headerBg: "var(--color-muted)",
        headerColor: "var(--color-muted-foreground)",
        rowHoverBg: "var(--color-muted)",
      },
      Modal: {
        contentBg: "var(--color-card)",
        headerBg: "var(--color-card)",
        footerBg: "var(--color-card)",
        titleColor: "var(--color-foreground)",
        titleFontSize: 16,
        borderRadiusLG: 12,
      },
    },
  };
}
